import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import {
  useChurchPlanner,
  usePlannerRoles,
  type PlannerAssignment,
  type PlannerRole,
  type PlannerServicePlan,
} from '../../hooks/useChurchPlanner';
import { hasAnyPermission, Permission } from '../../utils/roles';
import { supabase } from '../../services/supabase';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';
import { getPlannerRoleTeamGroup, resolveTeamIdFromRole } from '../../hooks/usePlannerInsights';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FaMusic as FaMusicRaw,
  FaVideo as FaVideoRaw,
  FaUsers as FaUsersRaw,
  FaCheck as FaCheckRaw,
  FaUser as FaUserRaw,
} from 'react-icons/fa';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaMusic: any = FaMusicRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaVideo: any = FaVideoRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUsers: any = FaUsersRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCheck: any = FaCheckRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUser: any = FaUserRaw;

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function areaIcon(area: string) {
  const a = area.toLowerCase();
  if (a.includes('worship') || a.includes('music') || a.includes('band')) return <FaMusic size={16} />;
  if (a.includes('production') || a.includes('media') || a.includes('video'))
    return <FaVideo size={16} />;
  return <FaUsers size={16} />;
}

function formatServiceWhen(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatAssignmentDate(value?: string | null) {
  if (!value) return 'Unknown date';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown date';
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getAssignmentEventTime(plan: PlannerServicePlan, assignment: PlannerAssignment): number {
  const eventValue = assignment.updated_at || assignment.created_at || plan.service_date;
  const parsed = eventValue ? new Date(eventValue).getTime() : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
}

function localDateKey(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function overlapsServiceDate(serviceDate: string, startsAt: string, endsAt: string): boolean {
  const serviceMs = new Date(serviceDate).getTime();
  const startMs = new Date(startsAt).getTime();
  const endMs = new Date(endsAt).getTime();
  if (Number.isNaN(serviceMs) || Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  return startMs <= serviceMs && endMs > serviceMs;
}

function assignmentForRole(
  plan: PlannerServicePlan | null,
  roleId: string
): PlannerAssignment | undefined {
  return plan?.service_plan_assignments?.find((a) => a.role_id === roleId);
}

const PageHeader = styled.div`
  margin-bottom: 0.25rem;
`;

const PlanSelectRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const Select = styled.select`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 10px;
  padding: 0.45rem 0.65rem;
  font-size: 0.85rem;
  min-width: 220px;
`;

const TeamCard = styled(PlannerCard)`
  margin-bottom: 1rem;
`;

const TeamHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const TeamHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const TeamTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: ${plannerColors.text};
`;

const TeamName = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
`;

const RoleBadge = styled.span`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: ${plannerColors.accent};
  background: ${plannerColors.accentSoft};
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
`;

const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
`;

const RoleSlot = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 14px;
  padding: 0.85rem 0.75rem;
  background: ${plannerColors.surfaceHover};
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-height: 118px;
`;

const RoleLabel = styled.p`
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${plannerColors.muted};
`;

const PersonName = styled.p`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${plannerColors.text};
`;

const AvatarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const Avatar = styled.div<{ $url?: string | null }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${plannerColors.surface};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  border: 1px solid ${plannerColors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${plannerColors.muted};
`;

const Unassigned = styled.span`
  font-style: italic;
  color: ${plannerColors.danger};
  font-size: 0.88rem;
`;

const RecentAssignmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-top: 0.3rem;
  padding-top: 0.55rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const RecentAssignmentsLabel = styled.span`
  font-size: 0.64rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${plannerColors.muted};
`;

const RecentAssignmentRow = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 0.45rem;
  align-items: center;
`;

const RecentAssignmentAvatar = styled(Avatar)`
  width: 28px;
  height: 28px;
  font-size: 0.68rem;
`;

const RecentAssignmentMeta = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.08rem;
`;

const RecentAssignmentName = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${plannerColors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RecentAssignmentServiceTitle = styled.span`
  font-size: 0.7rem;
  color: ${plannerColors.text};
  opacity: 0.82;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RecentAssignmentDate = styled.span`
  font-size: 0.68rem;
  color: ${plannerColors.muted};
`;

const AssignBtn = styled.button`
  align-self: flex-start;
  margin-top: auto;
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  border: none;
  background: ${plannerColors.accent};
  color: #1a1a1a;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    filter: brightness(1.05);
  }
`;

const AssignActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: auto;
`;

const UnassignBtn = styled(AssignBtn)`
  background: ${plannerColors.surface};
  color: ${plannerColors.text};
  border: 1px solid ${plannerColors.border};
`;

const TeamAutoAssignButton = styled.button`
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surface};
  color: ${plannerColors.text};
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${plannerColors.accent};
    color: ${plannerColors.accent};
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const PickerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1rem;
`;

const PickerCard = styled.div`
  width: min(560px, 100%);
  max-height: 80vh;
  background: ${plannerColors.surface};
  border: 1px solid ${plannerColors.border};
  border-radius: 14px;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const PickerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const PickerTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  color: ${plannerColors.text};
`;

const PickerClose = styled.button`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  border-radius: 8px;
  padding: 0.35rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
`;

const PickerSearch = styled.input`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  font-size: 0.88rem;
  padding: 0.55rem 0.65rem;
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  overflow-y: auto;
`;

const PickerItem = styled.button`
  border: 1px solid ${plannerColors.border};
  border-radius: 10px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  padding: 0.6rem 0.7rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: ${plannerColors.accent};
  }

  &:focus-visible {
    outline: 2px solid ${plannerColors.accent};
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PickerItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
`;

const PickerAvatar = styled.div<{ $url?: string | null }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background-color: ${plannerColors.surface};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  border: 1px solid ${plannerColors.border};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: ${plannerColors.muted};
  flex-shrink: 0;
`;

const PickerMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const PickerName = styled.div`
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PickerChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
`;

const PickerChip = styled.span`
  border: 1px solid ${plannerColors.border};
  border-radius: 999px;
  padding: 0.1rem 0.4rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: ${plannerColors.muted};
`;

const PickerAvailabilityChip = styled.span<{ $tone: 'available' | 'maybe' | 'unavailable' }>`
  border-radius: 999px;
  padding: 0.1rem 0.42rem;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'available'
        ? 'rgba(52, 199, 89, 0.45)'
        : $tone === 'maybe'
          ? 'rgba(255, 204, 0, 0.45)'
          : 'rgba(255, 69, 58, 0.45)'};
  color: ${({ $tone }) =>
    $tone === 'available'
      ? plannerColors.success
      : $tone === 'maybe'
        ? plannerColors.warning
        : plannerColors.danger};
  background: ${({ $tone }) =>
    $tone === 'available'
      ? 'rgba(52, 199, 89, 0.12)'
      : $tone === 'maybe'
        ? 'rgba(255, 204, 0, 0.12)'
        : 'rgba(255, 69, 58, 0.12)'};
`;

type AssignableMember = {
  id: string;
  full_name?: string | null;
  email?: string | null;
  title?: string | null;
  avatar?: string | null;
  roles?: string[] | null;
  default_service_plan_role_id?: string | null;
};

type RecentRoleAssignment = {
  assignmentId: string;
  name: string;
  avatar: string | null;
  serviceTitle: string;
  assignedAtLabel: string;
  eventTime: number;
};

const memberRoleLabelMap: Record<string, string> = {
  service_planner: 'Service Planner',
  service_planner_admin: 'Service Planner Admin',
};

const getInitialsFromText = (value: string) => {
  const text = value.trim();
  if (!text) return '?';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const getMemberInitials = (member: AssignableMember) => getInitialsFromText(member.full_name || member.email || '');

const getPlannerRoleChips = (member: AssignableMember) => {
  const roles = member.roles || [];
  return roles
    .filter((role) => role === 'service_planner' || role === 'service_planner_admin')
    .map((role) => memberRoleLabelMap[role] || role);
};

function availabilityMeta(status?: string | null): { tone: 'available' | 'maybe' | 'unavailable'; label: string } | null {
  const normalized = (status || '').trim().toLowerCase();
  if (!normalized) return null;
  if (normalized === 'available') return { tone: 'available', label: 'Available' };
  if (normalized === 'maybe') return { tone: 'maybe', label: 'Maybe' };
  if (normalized === 'unavailable') return { tone: 'unavailable', label: 'Unavailable' };
  return null;
}

export default function ChurchPlannerAssignmentsPage() {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const planner = useChurchPlanner(true, user?.id);
  const rolesQuery = usePlannerRoles(true);
  const [planId, setPlanId] = useState<string>('');
  const [pickerRole, setPickerRole] = useState<PlannerRole | null>(null);
  const [pickerAssignmentId, setPickerAssignmentId] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerMembers, setPickerMembers] = useState<AssignableMember[]>([]);
  const [autoAssigningGroupKey, setAutoAssigningGroupKey] = useState<string | null>(null);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageAssignments = hasAnyPermission(roles, [Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS]);
  const currentUserManagedTeamIds = useMemo(() => {
    if (!user?.id) return [] as string[];
    return Array.from(
      new Set(
        (planner.teamManagersQuery.data ?? [])
          .filter((row) => row.user_id === user.id)
          .map((row) => row.team_id)
          .filter(Boolean)
      )
    );
  }, [planner.teamManagersQuery.data, user?.id]);
  const currentUserManagedTeamIdSet = useMemo(() => new Set(currentUserManagedTeamIds), [currentUserManagedTeamIds]);
  const canManageScopedAssignments = canManageAssignments || currentUserManagedTeamIds.length > 0;

  const loadPickerMembers = useCallback(async (searchTerm: string) => {
    setPickerLoading(true);
    const term = searchTerm.trim();
    let query = supabase
      .from('users_directory')
      .select('id, full_name, email, title, avatar, roles, default_service_plan_role_id')
      .overlaps('roles', ['service_planner', 'service_planner_admin'])
      .order('full_name', { ascending: true })
      .limit(50);

    if (term) {
      query = query.or(`full_name.ilike.%${term}%,email.ilike.%${term}%`);
    }

    const { data, error } = await query;
    setPickerLoading(false);
    if (error) {
      window.alert(error.message || 'Unable to load planner members.');
      return;
    }
    const filteredMembers = ((data as AssignableMember[]) || []).filter((member) => {
      if (canManageAssignments) return true;
      const role = (rolesQuery.data ?? []).find((entry) => entry.id === member.default_service_plan_role_id);
      const teamId = resolveTeamIdFromRole(role);
      return Boolean(teamId && currentUserManagedTeamIdSet.has(teamId));
    });

    setPickerMembers(filteredMembers);
  }, [canManageAssignments, currentUserManagedTeamIdSet, rolesQuery.data]);

  const openAssignPicker = async (role: PlannerRole, assignment?: PlannerAssignment) => {
    if (!canManageScopedAssignments) return;
    setPickerRole(role);
    setPickerAssignmentId(assignment?.user_id ? assignment.id : null);
    setPickerSearch('');
    await loadPickerMembers('');
  };

  const closeAssignPicker = () => {
    setPickerRole(null);
    setPickerAssignmentId(null);
    setPickerSearch('');
    setPickerMembers([]);
  };

  const handlePickMember = async (member: AssignableMember) => {
    if (!selectedPlan || !pickerRole) return;
    const blockReason = getMemberBlockReason(member);
    if (blockReason) {
      window.alert(blockReason);
      return;
    }

    try {
      if (pickerAssignmentId) {
        await planner.updateAssignment({
          assignmentId: pickerAssignmentId,
          updates: { user_id: member.id, status: 'pending' },
        });
      } else {
        await planner.createAssignment({
          service_plan_id: selectedPlan.id,
          role_id: pickerRole.id,
          user_id: member.id,
          status: 'pending',
        });
      }
      closeAssignPicker();
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to assign member.');
    }
  };

  const handlePickerSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (pickerLoading || !pickerMembers.length) return;
    const firstAssignable = pickerMembers.find((member) => !getMemberBlockReason(member));
    if (!firstAssignable) return;
    void handlePickMember(firstAssignable);
  };

  useEffect(() => {
    if (!pickerRole) return;
    const handle = window.setTimeout(() => {
      loadPickerMembers(pickerSearch);
    }, 220);
    return () => window.clearTimeout(handle);
  }, [loadPickerMembers, pickerRole, pickerSearch]);

  const handleUnassign = async (assignment?: PlannerAssignment) => {
    if (!assignment?.id || !canManageScopedAssignments) return;
    try {
      await planner.deleteAssignment(assignment.id);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to unassign member.');
    }
  };

  const selectablePlans = useMemo(() => planner.upcomingPlans, [planner.upcomingPlans]);

  const selectedPlan = useMemo(() => {
    if (planId) {
      const found = selectablePlans.find((p) => p.id === planId);
      if (found) return found;
    }
    return planner.nextPlan ?? selectablePlans[0] ?? null;
  }, [planId, planner.nextPlan, selectablePlans]);

  const handleAutoAssignGroup = useCallback(async (groupKey: string, roles: PlannerRole[]) => {
    if (!selectedPlan || !canManageScopedAssignments) return;
    const roleIds = roles.map((role) => role.id).filter(Boolean);
    if (!roleIds.length) return;

    setAutoAssigningGroupKey(groupKey);
    try {
      await planner.autoAssignServicePlanRoles(selectedPlan.id, roleIds);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : 'Unable to auto-assign this team.');
    } finally {
      setAutoAssigningGroupKey(null);
    }
  }, [canManageScopedAssignments, planner, selectedPlan]);

  const availabilityByUserId = useMemo(() => {
    const map = new Map<string, string>();
    if (!selectedPlan?.id) return map;
    (planner.availability || []).forEach((entry) => {
      if (entry.service_plan_id !== selectedPlan.id || !entry.user_id) return;
      map.set(entry.user_id, entry.availability_status || '');
    });
    return map;
  }, [planner.availability, selectedPlan?.id]);

  const dayAssignedPlanIdsByUser = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!selectedPlan) return map;
    const targetDayKey = localDateKey(selectedPlan.service_date);
    planner.plans.forEach((plan) => {
      if (localDateKey(plan.service_date) !== targetDayKey) return;
      (plan.service_plan_assignments ?? []).forEach((assignment) => {
        if (!assignment.user_id) return;
        const planIds = map.get(assignment.user_id) ?? new Set<string>();
        planIds.add(plan.id);
        map.set(assignment.user_id, planIds);
      });
    });
    return map;
  }, [planner.plans, selectedPlan]);

  const selectedPlanAssignmentsByUser = useMemo(() => {
    const map = new Map<string, PlannerAssignment[]>();
    if (!selectedPlan) return map;
    (selectedPlan.service_plan_assignments ?? []).forEach((assignment) => {
      if (!assignment.user_id) return;
      const entries = map.get(assignment.user_id) ?? [];
      entries.push(assignment);
      map.set(assignment.user_id, entries);
    });
    return map;
  }, [selectedPlan]);

  const calendarUnavailableByUserId = useMemo(() => {
    const map = new Map<string, boolean>();
    if (!selectedPlan) return map;
    (planner.memberUnavailability ?? []).forEach((entry) => {
      if (!entry.user_id) return;
      if (!overlapsServiceDate(selectedPlan.service_date, entry.starts_at, entry.ends_at)) return;
      map.set(entry.user_id, true);
    });
    return map;
  }, [planner.memberUnavailability, selectedPlan]);

  const recentAssignmentsByRoleId = useMemo(() => {
    const map = new Map<string, RecentRoleAssignment[]>();

    planner.plans.forEach((plan) => {
      (plan.service_plan_assignments ?? []).forEach((assignment) => {
        if (!assignment.role_id || !assignment.user_id) return;
        const assignedUser = firstRelation(assignment.assigned_user);
        const name = assignedUser?.full_name?.trim() || assignedUser?.email?.trim() || 'Member';
        const entries = map.get(assignment.role_id) ?? [];
        entries.push({
          assignmentId: assignment.id,
          name,
          avatar: assignedUser?.avatar || null,
          serviceTitle: plan.title || 'Service',
          assignedAtLabel: formatAssignmentDate(assignment.updated_at || assignment.created_at || plan.service_date),
          eventTime: getAssignmentEventTime(plan, assignment),
        });
        map.set(assignment.role_id, entries);
      });
    });

    map.forEach((entries, roleId) => {
      map.set(
        roleId,
        [...entries]
          .sort((left, right) => {
            if (right.eventTime !== left.eventTime) return right.eventTime - left.eventTime;
            return left.assignmentId.localeCompare(right.assignmentId);
          })
          .slice(0, 3)
      );
    });

    return map;
  }, [planner.plans]);

  const getMemberBlockReason = (member: AssignableMember): string | null => {
    if (!selectedPlan || !pickerRole) return null;

    if ((member.default_service_plan_role_id || null) !== pickerRole.id) {
      return 'Default role does not match this slot.';
    }

    const availabilityStatus = (availabilityByUserId.get(member.id) || '').trim().toLowerCase();
    if (availabilityStatus === 'unavailable') {
      return 'Marked unavailable for this service.';
    }

    if (calendarUnavailableByUserId.get(member.id)) {
      return 'Blocked by calendar unavailability range.';
    }

    const samePlanAssignments = selectedPlanAssignmentsByUser.get(member.id) ?? [];
    const hasOtherSlotOnService = samePlanAssignments.some(
      (assignment) => assignment.id !== pickerAssignmentId
    );
    if (hasOtherSlotOnService) {
      return 'Already assigned to another slot in this service.';
    }

    const planIds = dayAssignedPlanIdsByUser.get(member.id);
    if (planIds && (planIds.size > 1 || (planIds.size === 1 && !planIds.has(selectedPlan.id)))) {
      return 'Already assigned to another service on this date.';
    }

    return null;
  };

  const { areaMap, planForTeams } = useMemo(() => {
    const roles = rolesQuery.data ?? [];
    const plan = selectedPlan;
    const map = new Map<string, { label: string; roles: PlannerRole[] }>();
    roles.forEach((role) => {
      const teamId = resolveTeamIdFromRole(role);
      if (!canManageAssignments && (!teamId || !currentUserManagedTeamIdSet.has(teamId))) {
        return;
      }
      const group = getPlannerRoleTeamGroup(role);
      const entry = map.get(group.key) ?? { label: group.label, roles: [] };
      entry.roles.push(role);
      map.set(group.key, entry);
    });
    return { areaMap: map, planForTeams: plan };
  }, [canManageAssignments, currentUserManagedTeamIdSet, rolesQuery.data, selectedPlan]);

  if (planner.isLoading || rolesQuery.isLoading) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerHeading>Assignments</PlannerHeading>
          <PlannerSubtle>Loading service data…</PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (planner.isError || rolesQuery.isError) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerHeading>Assignments</PlannerHeading>
          <PlannerError>Unable to load assignments.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  if (!canManageAssignments && !currentUserManagedTeamIds.length) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerHeading>Assignments</PlannerHeading>
          <PlannerError>You do not have access to any managed teams.</PlannerError>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <>
    <PlannerPage>
      <PageHeader>
        <PlannerKicker>
          {selectedPlan ? selectedPlan.title.toUpperCase() : 'NO SERVICE SELECTED'}
        </PlannerKicker>
        <PlannerHeading>Assignments</PlannerHeading>
        <PlannerSubtle>
          {selectedPlan
            ? formatServiceWhen(selectedPlan.service_date)
            : 'Create a service plan or select one below.'}
        </PlannerSubtle>
        <PlanSelectRow>
          <label htmlFor="planner-plan-select" style={{ fontSize: '0.8rem', color: plannerColors.muted }}>
            Service
          </label>
          <Select
            id="planner-plan-select"
            value={selectedPlan?.id ?? ''}
            onChange={(e) => setPlanId(e.target.value)}
          >
            {selectablePlans.length === 0 ? (
              <option value="">No plans</option>
            ) : (
              selectablePlans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} — {new Date(p.service_date).toLocaleDateString()}
                </option>
              ))
            )}
          </Select>
        </PlanSelectRow>
      </PageHeader>

      {!selectedPlan ? (
        <PlannerCard>
          <PlannerSubtle>No service plans available yet.</PlannerSubtle>
        </PlannerCard>
      ) : (
        <>
          {areaMap.size === 0 ? (
            <PlannerCard>
              <PlannerSubtle>
                No active service roles found. Add roles in the database (service_plan_roles) to populate team cards.
              </PlannerSubtle>
            </PlannerCard>
          ) : (
            Array.from(areaMap.entries())
            .sort(([, a], [, b]) => a.label.localeCompare(b.label))
            .map(([groupKey, group]) => (
              <TeamCard key={groupKey}>
                <TeamHeader>
                  <TeamTitleRow>
                    {areaIcon(group.label)}
                    <TeamName>{group.label}</TeamName>
                  </TeamTitleRow>
                  <TeamHeaderActions>
                    <RoleBadge>{group.roles.length} roles</RoleBadge>
                    {canManageScopedAssignments ? (
                      <TeamAutoAssignButton
                        type="button"
                        disabled={autoAssigningGroupKey === groupKey}
                        onClick={() => void handleAutoAssignGroup(groupKey, group.roles)}
                      >
                        {autoAssigningGroupKey === groupKey ? 'Auto-Assigning...' : 'Auto-Assign'}
                      </TeamAutoAssignButton>
                    ) : null}
                  </TeamHeaderActions>
                </TeamHeader>
                <RoleGrid>
                  {group.roles.map((role) => {
                    const asn = assignmentForRole(planForTeams, role.id);
                    const au = firstRelation(asn?.assigned_user);
                    const recentAssignments = recentAssignmentsByRoleId.get(role.id) ?? [];
                    const confirmed =
                      asn &&
                      ['confirmed', 'served'].includes((asn.status || '').toLowerCase());
                    return (
                      <RoleSlot key={role.id}>
                        <RoleLabel>{role.name}</RoleLabel>
                        {asn && au ? (
                          <>
                            <AvatarRow>
                              <Avatar $url={au.avatar}>
                                {!au.avatar ? <FaUser size={16} /> : null}
                              </Avatar>
                              {confirmed ? (
                                <FaCheck size={14} color={plannerColors.success} />
                              ) : null}
                            </AvatarRow>
                            <PersonName>{au.full_name || au.email || 'Member'}</PersonName>
                            {recentAssignments.length ? (
                              <RecentAssignmentsList>
                                <RecentAssignmentsLabel>Recent Assignments</RecentAssignmentsLabel>
                                {recentAssignments.map((entry) => (
                                  <RecentAssignmentRow key={entry.assignmentId}>
                                    <RecentAssignmentAvatar $url={entry.avatar}>
                                      {!entry.avatar ? getInitialsFromText(entry.name) : null}
                                    </RecentAssignmentAvatar>
                                    <RecentAssignmentMeta>
                                      <RecentAssignmentName title={entry.name}>{entry.name}</RecentAssignmentName>
                                      <RecentAssignmentServiceTitle title={entry.serviceTitle}>
                                        {entry.serviceTitle}
                                      </RecentAssignmentServiceTitle>
                                      <RecentAssignmentDate>{entry.assignedAtLabel}</RecentAssignmentDate>
                                    </RecentAssignmentMeta>
                                  </RecentAssignmentRow>
                                ))}
                              </RecentAssignmentsList>
                            ) : null}
                            {canManageScopedAssignments ? (
                              <AssignActions>
                                <AssignBtn type="button" onClick={() => openAssignPicker(role, asn)}>
                                  Change
                                </AssignBtn>
                                <UnassignBtn type="button" onClick={() => handleUnassign(asn)}>
                                  Unassign
                                </UnassignBtn>
                              </AssignActions>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <AvatarRow>
                              <Avatar>
                                <FaUser size={16} />
                              </Avatar>
                            </AvatarRow>
                            <Unassigned>Unassigned</Unassigned>
                            {recentAssignments.length ? (
                              <RecentAssignmentsList>
                                <RecentAssignmentsLabel>Recent Assignments</RecentAssignmentsLabel>
                                {recentAssignments.map((entry) => (
                                  <RecentAssignmentRow key={entry.assignmentId}>
                                    <RecentAssignmentAvatar $url={entry.avatar}>
                                      {!entry.avatar ? getInitialsFromText(entry.name) : null}
                                    </RecentAssignmentAvatar>
                                    <RecentAssignmentMeta>
                                      <RecentAssignmentName title={entry.name}>{entry.name}</RecentAssignmentName>
                                      <RecentAssignmentServiceTitle title={entry.serviceTitle}>
                                        {entry.serviceTitle}
                                      </RecentAssignmentServiceTitle>
                                      <RecentAssignmentDate>{entry.assignedAtLabel}</RecentAssignmentDate>
                                    </RecentAssignmentMeta>
                                  </RecentAssignmentRow>
                                ))}
                              </RecentAssignmentsList>
                            ) : null}
                            {canManageScopedAssignments ? (
                              <AssignActions>
                                <AssignBtn type="button" onClick={() => openAssignPicker(role, asn)}>
                                  Assign
                                </AssignBtn>
                                {asn?.id ? (
                                  <UnassignBtn type="button" onClick={() => handleUnassign(asn)}>
                                    Unassign
                                  </UnassignBtn>
                                ) : null}
                              </AssignActions>
                            ) : null}
                          </>
                        )}
                      </RoleSlot>
                    );
                  })}
                </RoleGrid>
              </TeamCard>
            ))
          )}

        </>
      )}
    </PlannerPage>
    {pickerRole ? (
      <PickerOverlay onClick={closeAssignPicker}>
        <PickerCard onClick={(event) => event.stopPropagation()}>
          <PickerHeader>
            <PickerTitle>Assign {pickerRole.name}</PickerTitle>
            <PickerClose type="button" onClick={closeAssignPicker}>Close</PickerClose>
          </PickerHeader>
          <PickerSearch
            value={pickerSearch}
            onChange={(event) => setPickerSearch(event.target.value)}
            onKeyDown={handlePickerSearchKeyDown}
            placeholder="Search by name or email"
          />
          <PickerList>
            {pickerLoading ? <PlannerSubtle>Loading planner members...</PlannerSubtle> : null}
            {!pickerLoading && !pickerMembers.length ? (
              <PlannerSubtle>No planner users found. Only Service Planner and Service Planner Admin roles are eligible.</PlannerSubtle>
            ) : null}
            {pickerMembers.map((member) => {
              const blockReason = getMemberBlockReason(member);
              return (
              <PickerItem
                key={member.id}
                type="button"
                disabled={Boolean(blockReason)}
                onClick={() => handlePickMember(member)}
              >
                <PickerItemRow>
                  <PickerAvatar $url={member.avatar}>
                    {!member.avatar ? getMemberInitials(member) : null}
                  </PickerAvatar>
                  <PickerMeta>
                    <PickerName>{member.full_name || member.email || member.id}</PickerName>
                    <PlannerSubtle style={{ margin: 0 }}>
                      {member.email || 'No email'}
                      {member.title ? ` • ${member.title}` : ''}
                    </PlannerSubtle>
                    <PickerChipRow>
                      {(() => {
                        const availability = availabilityMeta(availabilityByUserId.get(member.id));
                        if (!availability) return null;
                        return (
                          <PickerAvailabilityChip $tone={availability.tone}>
                            {availability.label}
                          </PickerAvailabilityChip>
                        );
                      })()}
                      {getPlannerRoleChips(member).map((chip) => (
                        <PickerChip key={`${member.id}-${chip}`}>{chip}</PickerChip>
                      ))}
                      {blockReason ? (
                        <PickerAvailabilityChip $tone="unavailable">
                          {blockReason}
                        </PickerAvailabilityChip>
                      ) : null}
                    </PickerChipRow>
                  </PickerMeta>
                </PickerItemRow>
              </PickerItem>
            );
            })}
          </PickerList>
        </PickerCard>
      </PickerOverlay>
    ) : null}
    </>
  );
}
